import { StyleFn } from '..'

/**
 * common input reset and minimal styling
 */
export const BareInput: StyleFn = ({ color, font, opacity }) => ({
  root: {
    // reset default input appearance
    boxSizing: 'border-box',
    margin: '0',
    width: '100%',
    border: 'none',
    appearance: 'none', // necessary for select
    fontFamily: 'inherit',
    outline: '0',
    background: 'transparent',
    // minimal styling
    fontSize: font.size.small,
    lineHeight: font.lineheight.small,
    color: color.getModalValues(color.text.primary),
    '&::placeholder': {
      color: color.getModalValues(color.text.tertiary),
    },
    '&:disabled': {
      opacity: opacity.disabled,
      userSelect: 'none',
      pointerEvents: 'none',
    },
  },
})

/**
 * common input look&feel and variants
 */
export const InputStyling: StyleFn = ({ color, font, opacity, size: { spacing } }) => {
  const colorVariant = (color: string) => ({
    boxShadow: `0 2px 0 0 ${color}`,
    '&:focus, &:focus-within': { boxShadow: `0 0 0 2px ${color}` },
  })
  return {
    root: {
      height: spacing[40],
      padding: spacing[8],
      backgroundColor: color.getModalValues(color.neutral.lighter),
      borderRadius: '3px 3px 0 0',
      boxShadow: `0 -1px 0 0 ${color.getModalValues(color.border.control)} inset`,
      transition: '0.1s ease-in-out',
      transitionProperty: 'box-shadow, border',
      '&:hover': {
        backgroundColor: color.getModalValues(color.neutral.light),
      },
      // increased specificity to override [data-theme=dark] styles
      '&$_valid&': colorVariant(color.feedback.success.default),
      '&$_invalid&': colorVariant(color.feedback.error.default),
    },
    _focused: {
      backgroundColor: color.getModalValues(color.neutral.light),
      borderColor: 'transparent',
      boxShadow: `0 0 0 2px ${color.getModalValues(color.border.focus)}`,
    },
    _disabled: {
      opacity: opacity.disabled,
      pointerEvents: 'none',
      // FIXME this cursor could be placed on form-control wrapper element
      // see https://stackoverflow.com/a/46665946/3641075
      // cursor: "not-allowed",
    },
    _valid: {},
    _invalid: {},
    _small: {
      paddingTop: spacing[4],
      paddingBottom: spacing[4],
      height: spacing[32],
      fontSize: font.size.small,
      lineHeight: font.lineheight.xsmall,
    },
    _medium: {},
    _large: {
      '& ': {
        height: spacing[56],
        fontSize: font.size.large,
      },
    },
  }
}

/**
 * Input component with adornments
 */
export const Input: StyleFn = (theme) => {
  const {
    size: { spacing },
  } = theme
  const { root: bareStyling } = BareInput(theme)
  const { root: rootStyling, _focused, _disabled, _large, ...inputVariants } = InputStyling(theme)

  return {
    root: {
      ...bareStyling,
      ...rootStyling,
      '&:focus, &:focus-within': _focused,
      '&:disabled': _disabled,
    },
    group: {
      display: 'flex',
      position: 'relative',
    },
    _large: {
      ..._large,
      '&$_adornedStart': {
        paddingLeft: spacing[40],
      },
      '&$_adornedEnd': {
        paddingRight: spacing[40],
      },
    },
    _adornedStart: {
      paddingLeft: spacing[32],
    },
    _adornedEnd: {
      paddingRight: spacing[32],
    },
    ...inputVariants,
  }
}

export const InputAdornment: StyleFn = ({ color, size }) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    zIndex: 1,
    padding: size.spacing[4],
    color: color.getModalValues(color.text.tertiary),
    top: 0,
    left: 'auto',
    right: 0,
    height: '100%',
  },
  _large: {
    '&$_start': {
      padding: `${size.spacing[16]} ${size.spacing[8]}`,
      '& svg': {
        width: size.spacing[24],
        height: size.spacing[24],
      },
    },
    '&$_end': {
      padding: `${size.spacing[8]} 0px`,
      '& svg': {
        width: size.spacing[24],
        height: size.spacing[24],
      },
    },
  },
  _start: {
    padding: `${size.spacing[12]} ${size.spacing[8]}`,
    left: 0,
    right: 'auto',
  },
  _end: {},
  _medium: {},
  _small: {
    padding: size.spacing[4],
    '&$_start': {
      padding: size.spacing[8],
    },
  },
})
